import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Bell, Mail, Smartphone, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";

const CHANNEL_META = {
  in_app: { label: "In-app", icon: Bell },
  email:  { label: "Email",  icon: Mail },
  push:   { label: "Push",   icon: Smartphone },
};

const KIND_LABELS = {
  payout_failed:         { title: "Payout failed",         severity: "critical" },
  payout_delayed:        { title: "Payout delayed",        severity: "warning" },
  banking_action_required:{ title: "Banking action required", severity: "critical" },
  verification_required: { title: "Verification required", severity: "critical" },
  invoice_paid:          { title: "Invoice paid",          severity: "info" },
  estimate_viewed:       { title: "Estimate viewed",       severity: "info" },
  review_request:        { title: "Review request",        severity: "info" },
  customer_reply:        { title: "Customer reply",        severity: "critical" },
};

const SEV_TONE = {
  critical: "bg-rose-50 border-rose-200 text-rose-700",
  warning:  "bg-amber-50 border-amber-200 text-amber-700",
  info:     "bg-zinc-50 border-zinc-200 text-zinc-700",
};

export default function NotificationPreferencesPanel() {
  const [prefs, setPrefs] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/notifications/preferences");
      setPrefs(data.preferences);
      setKinds(data.kinds || []);
      setChannels(data.channels || []);
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load preferences");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = (kind, channel) => {
    setPrefs((cur) => ({
      ...cur,
      [kind]: { ...cur[kind], [channel]: !cur[kind]?.[channel] },
    }));
    setDirty(true);
  };
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/notifications/preferences", { preferences: prefs });
      toast.success("Notification preferences saved");
      setDirty(false);
    } catch (e) {
      toast.error("Could not save");
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6 mb-6 card-crisp" data-testid="notif-prefs-loading">
      <Loader2 className="w-4 h-4 animate-spin"/>
    </div>
  );

  return (
    <div className="bg-white border border-zinc-200 rounded-xl card-crisp overflow-hidden mb-6" data-testid="notif-prefs-panel">
      <div className="p-6 border-b border-zinc-100 flex items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold">Notification preferences</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Pick a delivery channel for each notification type.</p>
        </div>
        <Button
          onClick={save}
          disabled={!dirty || saving}
          className="bg-[#FF5A00] hover:bg-[#E04F00] text-white btn-lift disabled:opacity-40"
          data-testid="notif-prefs-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin"/> : <Save className="w-4 h-4 mr-1"/>}
          Save
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase font-mono-accent tracking-widest text-zinc-500 border-b border-zinc-100">
              <th className="text-left px-6 py-3">Notification</th>
              {channels.map((c) => {
                const Icon = CHANNEL_META[c]?.icon || Bell;
                return (
                  <th key={c} className="text-center px-4 py-3">
                    <div className="inline-flex items-center gap-1">
                      <Icon className="w-3.5 h-3.5"/> {CHANNEL_META[c]?.label || c}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {kinds.map((k) => {
              const meta = KIND_LABELS[k] || { title: k.replace(/_/g, " "), severity: "info" };
              return (
                <tr key={k} className="border-b border-zinc-100 last:border-0" data-testid={`notif-prefs-row-${k}`}>
                  <td className="px-6 py-3">
                    <div className="font-medium">{meta.title}</div>
                    <span className={`inline-block mt-1 text-[10px] uppercase tracking-widest font-mono-accent px-2 py-0.5 rounded-full border ${SEV_TONE[meta.severity]}`}>
                      {meta.severity}
                    </span>
                  </td>
                  {channels.map((c) => (
                    <td key={c} className="text-center px-4 py-3">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!prefs?.[k]?.[c]}
                          onChange={() => toggle(k, c)}
                          className="w-4 h-4 accent-[#FF5A00]"
                          data-testid={`notif-prefs-${k}-${c}`}
                        />
                      </label>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
